import React from 'react';
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconPlus } from '@tabler/icons';
import { formatDistanceToNow, parseJSON, format } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import { useSupportCases } from '../../data/support/useSupportCustomer';
import {
  SupportCaseFilters,
  SupportCaseStatus,
} from '../../types/support.types';
import AQDataGrid from '../AQDataGrid';
import CaseStatusChip from '../internal/support/CaseStatusChip';
import CreateCaseDialog from './CreateCaseDialog';

const STATUS_OPTIONS: SupportCaseStatus[] = [
  'open',
  'pending',
  'waiting_on_customer',
  'resolved',
  'closed',
];

export default function SupportCases() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<SupportCaseStatus[]>(
    [],
  );
  const [searchInput, setSearchInput] = React.useState('');
  const [searchDebounced, setSearchDebounced] = React.useState('');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>();

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchDebounced(value), 300);
  };

  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const filters: SupportCaseFilters = React.useMemo(() => {
    const f: SupportCaseFilters = {};
    if (statusFilter.length) f.status = statusFilter;
    if (searchDebounced) f.search = searchDebounced;
    return f;
  }, [statusFilter, searchDebounced]);

  const casesQuery = useSupportCases(undefined, filters);
  const cases = casesQuery.data || [];
  const rows = React.useMemo(() => cases.map((c) => ({ ...c })), [cases]);

  const colDefs = React.useMemo(
    () => [
      {
        field: 'case_number',
        headerName: 'Case #',
        width: 100,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {params.value != null ? `#${params.value}` : '—'}
          </Typography>
        ),
      },
      {
        field: 'subject',
        headerName: 'Subject',
        flex: 1,
        minWidth: 200,
        sortable: true,
        filterable: true,
      },
      {
        field: 'priority',
        headerName: 'Priority',
        width: 120,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <CaseStatusChip value={params.value} type="priority" />
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <CaseStatusChip value={params.value} type="status" />
        ),
      },
      {
        field: 'created_at',
        headerName: 'Created',
        width: 160,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Tooltip
            title={format(parseJSON(params.value), 'PPpp')}
            placement="top"
          >
            <Typography variant="body2" color="text.secondary">
              {formatDistanceToNow(parseJSON(params.value), {
                addSuffix: true,
              })}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'updated_at',
        headerName: 'Last Updated',
        width: 160,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Tooltip
            title={format(parseJSON(params.value), 'PPpp')}
            placement="top"
          >
            <Typography variant="body2" color="text.secondary">
              {formatDistanceToNow(parseJSON(params.value), {
                addSuffix: true,
              })}
            </Typography>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  const gridProps = React.useMemo(
    () => ({
      rowHeight: 52,
      sortingMode: 'client' as const,
      filterMode: 'client' as const,
      paginationMode: 'client' as const,
      disableColumnMenu: true,
      disableRowSelectionOnClick: true,
      initialState: {
        sorting: {
          sortModel: [{ field: 'created_at', sort: 'desc' as const }],
        },
      },
      onRowClick: (params: any) => {
        navigate(`/${accountId}/support/${params.id}`);
      },
      sx: {
        cursor: 'pointer',
        '& .MuiDataGrid-cell': {
          display: 'flex',
          alignItems: 'center',
        },
      },
    }),
    [navigate, accountId],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          maxWidth: 1200,
          margin: '0 auto 24px',
        }}
      >
        <Typography variant="h5">Support Cases</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<IconPlus size={16} />}
          onClick={() => setCreateOpen(true)}
        >
          New Case
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Status"
          size="small"
          select
          SelectProps={{
            multiple: true,
            renderValue: (selected: any) =>
              (selected as SupportCaseStatus[]).map((s) => (
                <CaseStatusChip key={s} value={s} type="status" />
              )),
          }}
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as unknown as SupportCaseStatus[])
          }
          sx={{ minWidth: 180 }}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              <CaseStatusChip value={s} type="status" />
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Search"
          size="small"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ minWidth: 200 }}
        />
      </Box>

      <AQDataGrid
        id="support-cases"
        rows={rows}
        colDefs={colDefs}
        loading={casesQuery.isLoading}
        gridProps={gridProps}
        customErrorElement={
          casesQuery.isError ? (
            <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
              Failed to load support cases
            </Typography>
          ) : undefined
        }
      />

      <CreateCaseDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Box>
  );
}
